import { useTranslation } from "@i18n";
import React, { useEffect, useState, forwardRef, useMemo } from "react";
import IconComponent from "@CubeComponents/icons";
import { useResizeDetector } from "react-resize-detector";
import Button from "@mui/material/Button";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ToolTipComponent from "../tooltip";
import SortGroupModal from "./Modals/SortGroupModal";
import FilterModal from "./Modals/FilterModal";
import GridColumnChooser from "./GridColumnChooser";
import Text from "../Text/Text";
import RowResizeModal from "./RowResizeModal";
import { exportGroupedExcel } from ".";
import { TOOLBAR_PROPERTIES_ACTION } from "./toolbarUtils";
import _, { get } from "lodash";
import Views from "@Components/ViewsNewFlow";
import { getFormatterByType } from "./Formatters/FormattersRegistery";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import type { Column } from "slickgrid-react";
import type { TranslatedString } from "@i18n";
import type {
  AppliedFilterCondition,
  GridRowBase,
  GroupCondition,
  SlickGrid1Ref,
  SlickgridReactInstanceX,
  SortCondition,
  ToolbarItem,
  FilterCondition as GridFilterCondition,
} from ".";
import type { FilterModalProps } from "./Modals/FilterModal";
import type { SortGroupModalProps } from "./Modals/SortGroupModal";
import type { GridLike } from "./Formatters/Formatters";
import type { VirtualAnchorElement } from "./OptionDropDown";

/**
 * A toolbar entry as this component renders it. The exported `ToolbarItem` calls the label `text`
 * and does not declare the Syncfusion-era keys below, which ~6 call sites still pass by extending
 * `ToolbarItem` locally - so they are named here rather than widened on the public type.
 */
type ToolBarEntry = ToolbarItem & {
  name?: React.ReactNode;
  tooltip?: React.ReactNode;
  hidden?: boolean;
  buttonType?: string;
  prefixIconTemplate?: React.ReactNode;
  suffixIconTemplate?: React.ReactNode;
};

/**
 * A grid column as this toolbar reads it. Deliberately structural rather than an intersection with
 * slickgrid's `Column<T>`: the same annotation has to accept both a column read straight off the
 * grid instance (`Column<any>`, whose `field` is a plain string) and one of the grid's own
 * definitions (whose `field` is `keyof TRow`), and no single instantiation of `Column<T>` is a
 * supertype of both.
 */
type ToolBarColumn = {
  id?: string | number;
  field?: string;
  width?: number;
  formatter?: unknown;
  formatterType?: string;
  clipMode?: unknown;
  /** Nullable to match slickgrid's own `Column`, whose columns are read through this type. */
  headerCssClass?: string | null;
  excludeFromColumnPicker?: boolean;
  initiallyHidden?: boolean;
  _valueGetter?: (row: GridRowBase) => unknown;
};

/** Column definitions as the grid hands them to the toolbar and forwards to the filter/sort modals. */
type ToolBarColumnDefinition = {
  id?: string | number;
  /** `keyof TRow` on the grid side, so widened to PropertyKey rather than `string` here. */
  field?: PropertyKey;
  name?: string;
  filterType?: string;
  disableFiltering?: boolean;
  disableSorting?: boolean;
  disableGrouping?: boolean;
};

/** Props of `<Views>` as this toolbar forwards them; see the KNOWN MISMATCH note at the call site. */
type ViewsProps = React.ComponentProps<typeof Views>;

/**
 * The toolbar's props. Every entry is optional: SlickGrid1 supplies the whole set, but the Gantt
 * (`Schedule/Gantt/indexNew.tsx`) mounts the same component with only the toolbar + views props, so
 * the ones it omits are asserted non-null at their single use rather than defaulted — a default
 * would emit.
 *
 * Several members are `unknown` because the grid and the modal on the far side genuinely disagree
 * about their shape (response envelopes, predicate maps, the views payload). Each is asserted at the
 * one JSX site that forwards it, with the disagreement noted there.
 */
interface ToolBarProps {
  toolbar?: ToolBarEntry[];
  /** Method shorthand: SlickGrid1 hands this a handler declared `(cmd: string) => void`. */
  toolbarClickHandler?(id?: string): void;
  showToolbarBorder?: boolean;
  enableColumnChooser?: boolean;
  enableSearchBar?: boolean;
  searchPlaceholder?: string;
  searchTerm?: string;
  onSearchChange?(next: string): void;
  searchDebounceMs?: number;
  columnDefinitions?: ToolBarColumnDefinition[] | null;
  propertiesPanel?: boolean;
  propertiesDisabled?: boolean;
  height?: number | string;
  excelExportServiceRef?: React.MutableRefObject<boolean>;
  excelExportExtension?: { exportToExcel: () => void } | null;
  /** `column` stays `unknown`: the grid's own column type is neither sub- nor supertype of ours. */
  ellipseFormatterWithTooltip?(args: { value: unknown; column?: unknown }): HTMLElement;
  excelExportCallback?: () => void;
  excelExport?: boolean;
  excelExportDisabled?: boolean;
  enableFilteringOption?: boolean;
  enableSortingOption?: boolean;
  enableGroupingOption?: boolean;
  enableRefreshOption?: boolean;
  refreshOptionCallBack?: () => void;
  modalState?: string;
  setModalState?(state: string): void;
  stateRef?: React.MutableRefObject<{ enableWrapText?: boolean } | null>;
  /** The grid also anchors modals to a virtual (rect-only) element, e.g. a right-clicked cell. */
  modalAnchor?: HTMLElement | VirtualAnchorElement | null;
  sortConditions?: SortCondition[];
  setSortConditions?: React.Dispatch<React.SetStateAction<SortCondition[]>>;
  filterConditions?: AppliedFilterCondition[];
  setFilterConditions?: React.Dispatch<React.SetStateAction<AppliedFilterCondition[]>>;
  /** The grid's remote source: a URL, or the fetcher it wraps one in. */
  remoteDataSource?: unknown;
  callRemote?(source: string, body: { value: unknown }): Promise<unknown>;
  beforeDataBound?: unknown;
  datasetForFetchingOptions?: unknown[];
  query?: unknown;
  getUpdatedFilterConditions?: FilterModalProps["getUpdatedFilterConditions"];
  applyFilterFunction?: FilterModalProps["applyFilterFunction"];
  setAppliedFilters?: React.Dispatch<React.SetStateAction<string[]>>;
  appliedFilters?: string[];
  setFilterPredicates?: React.Dispatch<React.SetStateAction<Record<string, unknown> | string>>;
  setFilterConditionsObject?: React.Dispatch<React.SetStateAction<Record<string, GridFilterCondition>>>;
  groupConditions?: GroupCondition[];
  setGroupConditions?: React.Dispatch<React.SetStateAction<GroupCondition[]>>;
  enableRowResize?: boolean;
  handleRowResizeOptionClick?: (size: string, height: number) => void;
  currentColumnId?: string;
  viewsOptions?: unknown;
  enableViews?: boolean;
  onViewsLoaded?: unknown;
  createNewLayoutData?: unknown;
  triggerUpdateCurrentView?: unknown;
  onClickLayout?: unknown;
  setTempCondition?: React.Dispatch<React.SetStateAction<AppliedFilterCondition | null>>;
  infiniteScroll?: boolean;
  tempCondition?: AppliedFilterCondition | null;
  setEnableWrapText?: (value: boolean) => void;
  enableWrapText?: boolean;
  specialColumns?: Array<{ id?: string | number }>;
  onSaveColumns?(cols: unknown[]): void;
  gridId?: string;
  /** Forwarded by SlickGrid1 but not read here; declared so the JSX attribute is not excess. */
  sortDataset?: unknown;
  dataset?: unknown;
  filterType?: unknown;
  localData?: unknown;
  callbackForVisibleColumnsUpdate?: unknown;
}

/** Props of the debounced search box rendered inside the toolbar. */
interface GridSearchInputProps {
  value?: string;
  placeholder?: string;
  onSearchChange?: (next: string) => void;
  debounceMs?: number;
}

type RowSizeKey = "small" | "medium" | "large" | "wrap";

const GridSearchInput = ({ value = "", placeholder, onSearchChange, debounceMs = 300 }: GridSearchInputProps) => {
  const { t } = useTranslation();
  const [local, setLocal] = useState<string>(value ?? "");
  const [expanded, setExpanded] = useState<boolean>(Boolean(value));
  const timerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    setLocal(value ?? "");
    if (value) setExpanded(true);
  }, [value]);

  useEffect(
    () => () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    },
    [],
  );

  const emit = (next: string) => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => onSearchChange?.(next), debounceMs);
  };

  const handleExpand = () => {
    setExpanded(true);
    requestAnimationFrame(() => inputRef.current?.focus());
  };

  const handleBlur = () => {
    if (!local) setExpanded(false);
  };

  if (!expanded) {
    return (
      <ToolTipComponent content={t("Search")}>
        <ButtonComponent
          className="me-2"
          buttonType={BUTTON_TYPES.TEXT_BUTTON}
          iconButton={true}
          onClick={handleExpand}
        >
          <IconComponent name="SearchIcon" width="1.5rem" height="1.5rem" />
        </ButtonComponent>
      </ToolTipComponent>
    );
  }

  return (
    <div className="flex items-center min-w-[10rem] max-w-[16rem] me-2">
      <TextFieldComponent
        id="gridSearchBox"
        ref={inputRef}
        value={local}
        placeholder={placeholder || t("Search")}
        onBlur={handleBlur}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
          const next = e.target.value;
          setLocal(next);
          emit(next);
        }}
        slotProps={{
          input: {
            // Show a clear (cross) icon while there is text, so the user can wipe the
            // search in one click; fall back to the search icon when the field is empty.
            endAdornment: local ? (
              <IconComponent
                name="cross"
                width="1.25rem"
                height="1.25rem"
                toolTipContent={t("Clear")}
                onClick={() => {
                  if (timerRef.current) clearTimeout(timerRef.current);
                  setLocal("");
                  onSearchChange?.("");
                  requestAnimationFrame(() => inputRef.current?.focus());
                }}
              />
            ) : (
              <IconComponent name="SearchIcon" width="1.25rem" height="1.25rem" />
            ),
          },
        }}
      />
    </div>
  );
};

const getRowIcon = (size: string) => {
  let iconName: string;
  if (size === "small") {
    iconName = "RowResizeIcon";
  } else if (size === "wrap") {
    iconName = "WrapText";
  } else if (size === "medium") {
    iconName = "MediumCell";
  } else {
    iconName = "LargeRowSize";
  }
  return <IconComponent name={iconName} width="1.5rem" height="1.5rem" />;
};

const ToolBar = forwardRef(
  (
    {
      toolbar = [],
      toolbarClickHandler,
      showToolbarBorder = false,
      enableColumnChooser = false,
      enableSearchBar = false,
      searchPlaceholder = "",
      searchTerm = "",
      onSearchChange,
      searchDebounceMs = 300,
      columnDefinitions = [],
      propertiesPanel,
      propertiesDisabled = false,
      height = 24,
      excelExportServiceRef,
      excelExportExtension,
      ellipseFormatterWithTooltip,
      excelExportCallback,
      excelExport,
      excelExportDisabled = false,
      enableFilteringOption = false,
      enableSortingOption = false,
      enableGroupingOption = false,
      enableRefreshOption = false,
      refreshOptionCallBack,
      modalState,
      setModalState,
      stateRef,
      modalAnchor,
      sortConditions,
      setSortConditions,
      filterConditions,
      setFilterConditions,
      remoteDataSource,
      callRemote,
      beforeDataBound,
      datasetForFetchingOptions,
      query,
      getUpdatedFilterConditions,
      applyFilterFunction,
      setAppliedFilters,
      appliedFilters,
      setFilterPredicates,
      setFilterConditionsObject,
      groupConditions,
      setGroupConditions,
      enableRowResize,
      handleRowResizeOptionClick,
      currentColumnId,
      viewsOptions,
      enableViews,
      onViewsLoaded,
      createNewLayoutData,
      triggerUpdateCurrentView,
      onClickLayout,
      setTempCondition,
      infiniteScroll,
      tempCondition,
      setEnableWrapText,
      enableWrapText,
      specialColumns,
      onSaveColumns,
      gridId,
    }: ToolBarProps,
    ref: React.ForwardedRef<unknown>,
  ) => {
    const { t } = useTranslation();
    const [showOverFlowBtns, setShowOverFlowBtns] = useState<boolean>(false);
    const { width, ref: toolRef } = useResizeDetector();
    // MUI accepts a virtual anchor at runtime; the modals below type the slot as HTMLElement only.
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>((modalAnchor as HTMLElement) || null);
    const [showPropertiesPanel, setShowPropertiesPanel] = useState<boolean>(false);
    const [showColumnChooser, setShowColumnChooser] = useState<boolean>(false);
    const [showRowResize, setShowRowResize] = useState<boolean>(false);
    const [currentRowSize, setCurrentRowSize] = useState<RowSizeKey>("small");
    // SlickGrid1 forwards the mounted grid instance here (the toolbar is not a DOM ref target), so
    // the ref is re-stated as that instance. Kept as an intersection with the incoming
    // `ForwardedRef` so the value is still accepted wherever a plain `Ref` is expected.
    const gridRef = ref as typeof ref & React.RefObject<SlickgridReactInstanceX<GridRowBase> | null>;

    const toggleGrouping = (toggleType: string) => {
      if (toggleType === "expand") {
        gridRef.current?.dataView.expandAllGroups();
      } else {
        gridRef.current?.dataView.collapseAllGroups();
      }
    };
    useEffect(() => {
      if (toolRef?.current) {
        const ele = toolRef.current;
        if (ele.scrollWidth - ele.scrollLeft === ele.clientWidth) {
          setShowOverFlowBtns(false);
        } else {
          setShowOverFlowBtns(true);
        }
      }
    }, [width, toolRef]);
    useEffect(() => {
      setAnchorEl(null);
    }, [modalAnchor]);
    const rightArray = useMemo(
      () => toolbar?.filter((item: ToolBarEntry) => item.rightAllign && !item.sticky),
      [toolbar],
    );
    const leftArray = useMemo(
      () => toolbar?.filter((item: ToolBarEntry) => !item.rightAllign && !item.sticky),
      [toolbar],
    );
    const rightStickyArray = useMemo(
      () => toolbar?.filter((item: ToolBarEntry) => item.rightAllign && item.sticky),
      [toolbar],
    );
    const leftStickyArray = useMemo(
      () => toolbar?.filter((item: ToolBarEntry) => !item.rightAllign && item.sticky),
      [toolbar],
    );

    const handleArrowClick = (direction: string) => {
      if (toolRef.current) {
        const scrollAmount = direction === "forward" ? 200 : -200;
        toolRef.current.scrollBy({ left: scrollAmount, behavior: "smooth" });
      }
    };

    const viewsTemplate = () => {
      return (
        <div className="flex">
          {/* KNOWN MISMATCH, left as-is: <Views> declares `onClickLayout`/`onViewsLoaded` against
              the saved-view types in @Types/layout and `triggerUpdateCurrentView` as `object`, while
              the grid passes its own LayoutData and `Record | false`. The values are forwarded
              verbatim; only the disagreement is asserted away. */}
          <Views
            {...(viewsOptions as ViewsProps)}
            onClickLayout={onClickLayout as ViewsProps["onClickLayout"]}
            createNewLayoutData={createNewLayoutData}
            triggerUpdateCurrentView={triggerUpdateCurrentView as ViewsProps["triggerUpdateCurrentView"]}
            onViewsLoaded={onViewsLoaded as ViewsProps["onViewsLoaded"]}
          />
          <div className="w-px h-8 bg-[#666A84] mx-1" />
        </div>
      );
    };

    const handleClick = (id: string | undefined) => {
      if (toolbarClickHandler) {
        toolbarClickHandler(id);
      }
    };

    const handleExportToExcel = () => {
      if (remoteDataSource && infiniteScroll) {
        excelExportServiceRef!.current = true;
        // Use the provided callback, or fall back to grid refresh which
        // loads ALL data (NO_OF_FETCHED_ROWS) and triggers the export
        // in the loadData data-bound handler.
        if (excelExportCallback) {
          excelExportCallback();
        } else {
          gridRef.current?.refresh!();
        }
      } else {
        if (groupConditions?.length! > 0) {
          exportGroupedExcel(gridRef);
        } else {
          excelExportExtension?.exportToExcel();
        }
      }
    };

    const handleToggleProperties = () => {
      setShowPropertiesPanel((prev: boolean) => !prev);
      if (toolbarClickHandler) {
        toolbarClickHandler(TOOLBAR_PROPERTIES_ACTION);
      }
    };

    const getFormatterType = (column: ToolBarColumn) => {
      if (column.formatterType) {
        const registeredFormatter = getFormatterByType(column.formatterType);
        if (registeredFormatter && typeof registeredFormatter === "function") {
          return function (
            row: number,
            cell: number,
            value: unknown,
            columnDef: ToolBarColumn,
            dataContext: GridRowBase,
            grid?: GridLike,
          ) {
            if (dataContext?.__isDummy) return "";
            try {
              return registeredFormatter(
                row,
                cell,
                value,
                columnDef as Parameters<typeof registeredFormatter>[3],
                dataContext,
                grid,
              );
            } catch (error) {
              console.error(`Formatter error for type "${column.formatterType}":`, error);
              return value ?? "";
            }
          };
        }
      }
    };

    const renderToolbarItem = (item: ToolBarEntry, index: number) => {
      return (
        <div
          className={`flex items-center mr-2 ${
            item.disabled
              ? "opacity-40 cursor-not-allowed grayscale select-none pointer-events-auto *:pointer-events-none"
              : ""
          } ${item.hidden ? " hidden" : ""}`}
          key={index}
          onClick={item.template && !item.disabled ? () => handleClick(item.id) : undefined}
        >
          {!item.template && (
            <ButtonComponent
              // Every toolbar key is forwarded verbatim; `name` is widened to ReactNode here but is
              // only ever a string at the sites that set it, so the string arm is selected rather
              // than dropping the prop (dropping it would emit differently).
              {...(item as ToolBarEntry & { name?: string })}
              key={item.id}
              sx={{ flexShrink: 0 }}
              className={`${index === 0 ? "ms-3" : ""} ${" " + item.cssClass} `}
              buttonType={item.buttonType || BUTTON_TYPES.BACKGROUND_BUTTON}
              onClick={() => {
                handleClick(item.id);
              }}
            >
              <div className="flex items-center">
                {(item.prefixIcon || item.prefixIconTemplate) && (
                  <div className="flex items-center me-1">
                    {item.prefixIcon && <IconComponent name={item.prefixIcon} width="1rem" height="1rem" />}
                    {item.prefixIconTemplate}
                  </div>
                )}
                <Text className="inline">{item.name}</Text>
                {(item.suffixIcon || item.suffixIconTemplate) && (
                  <div className="flex items-center ms-1">
                    {item.suffixIcon && <IconComponent name={item.suffixIcon} width="1.5rem" height="1.5rem" />}
                    {item.suffixIconTemplate}
                  </div>
                )}
              </div>
            </ButtonComponent>
          )}
          {item?.template?.(item?.props ? item.props : {})}
        </div>
      );
    };

    const openFilteringModal = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setModalState!("Filter");
    };
    const openGroupingModal = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setModalState!("Group");
    };
    const openSortingModal = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
      setModalState!("Sort");
    };

    const openRowResizeModal = (event: React.MouseEvent<HTMLElement>) => {
      setShowRowResize(true);
      setAnchorEl(event.currentTarget);
    };
    return (
      <>
        {(toolbar?.length > 0 ||
          enableFilteringOption ||
          enableSortingOption ||
          enableGroupingOption ||
          enableColumnChooser ||
          enableRowResize ||
          excelExport ||
          enableViews ||
          enableRefreshOption ||
          enableSearchBar) && (
          <div
            className={`overflow-hidden flex gap-6 items-center bg-[#FAFCFF] ${
              showToolbarBorder && "border-b border-stroke_primary_light"
            } h-${height}`}
          >
            {showOverFlowBtns && (
              <div className="flex-shrink-0 flex items-center h-full cursor-pointer">
                <Button
                  sx={{
                    height: "100%",
                    minWidth: "2.25rem",
                    borderInlineEnd: "1px solid #D1D5DB",
                    borderRadius: "0.5rem 0 0 0.5rem",
                    color: "#6B7280",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#E5E7EB",
                    },
                  }}
                  onClick={() => handleArrowClick("backward")}
                  size="small"
                >
                  <ArrowBackIosNewIcon sx={{ fontSize: "1.125rem" }} />
                </Button>
              </div>
            )}
            <div
              ref={toolRef}
              className="flex-grow flex items-center justify-between h-16 max-w-full gap-1 overflow-hidden"
            >
              <div className="flex  items-center">
                {enableViews && viewsTemplate()}
                {leftArray?.map((item: ToolBarEntry, index: number) => {
                  if (item.tooltip) {
                    return (
                      <ToolTipComponent key={`start-${item.id}-${index}`} content={item.tooltip as TranslatedString}>
                        {renderToolbarItem(item, index)}
                      </ToolTipComponent>
                    );
                  }
                  return renderToolbarItem(item, index);
                })}
                {excelExport && (
                  <ToolTipComponent content={t("Export to Excel")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={handleExportToExcel}
                      disabled={excelExportDisabled}
                    >
                      <div className="flex gap-1 items-center">
                        <IconComponent name="ExcelExportIcon" width="1rem" height="1rem" />
                        <Text className="inline">{t("Export")}</Text>
                      </div>
                    </ButtonComponent>
                  </ToolTipComponent>
                )}
              </div>

              <div className="flex items-center">
                {rightArray?.map((item: ToolBarEntry, index: number) => {
                  if (item.tooltip) {
                    return (
                      <ToolTipComponent
                        key={`end-scrollable-${item.id}-${index}`}
                        content={item.tooltip as TranslatedString}
                      >
                        {renderToolbarItem(item, index)}
                      </ToolTipComponent>
                    );
                  }
                  return renderToolbarItem(item, index);
                })}
              </div>
            </div>

            <div className="shrink-0 flex items-center">
              {leftStickyArray?.map((item: ToolBarEntry, index: number) => {
                if (item.tooltip) {
                  return (
                    <ToolTipComponent
                      key={`end-scrollable-${item.id}-${index}`}
                      content={item.tooltip as TranslatedString}
                    >
                      {renderToolbarItem(item, index)}
                    </ToolTipComponent>
                  );
                }
                return renderToolbarItem(item, index);
              })}
              {enableSearchBar && (
                <GridSearchInput
                  value={searchTerm}
                  placeholder={searchPlaceholder}
                  onSearchChange={onSearchChange}
                  debounceMs={searchDebounceMs}
                />
              )}

              {enableRefreshOption && (
                <div id={gridId ? `refresh-icon-${gridId}` : "refresh-icon"}>
                  <ToolTipComponent content={t("Refresh")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      iconButton={true}
                      data-testid="gridRefreshOption"
                      // Caller owns refresh via refreshOptionCallBack; fall back to the grid's own
                      // data reload when no callback is provided.
                      onClick={() => (refreshOptionCallBack ? refreshOptionCallBack() : gridRef.current?.refresh?.())}
                    >
                      <IconComponent name="refresh" width="1.5rem" height="1.5rem" />
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}

              {enableGroupingOption && (
                <div id={gridId ? `grouping-icon-${gridId}` : "grouping-icon"}>
                  <ToolTipComponent content={t("Grouping")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={openGroupingModal}
                      iconButton={true}
                    >
                      <IconComponent name="Grouping" width="1.5rem" height="1.5rem" />

                      {groupConditions?.length! > 0 && (
                        <div
                          aria-label="Active sort conditions"
                          style={{
                            position: "absolute",
                            top: 0,
                            insetInlineEnd: 0,
                            width: "0.5rem",
                            height: "0.5rem",
                            borderRadius: "6.25rem",
                            background: "var(--Notification-notification-primary, #6BBBE3)",
                            transform: "translate(-50%, 50%)",
                            zIndex: 10,
                          }}
                        />
                      )}
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}
              {enableFilteringOption && (
                <div id={gridId ? `filtering-icon-${gridId}` : "filtering-icon"}>
                  <ToolTipComponent content={t("Filter")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={openFilteringModal}
                      iconButton={true}
                    >
                      <IconComponent name="Filter" width="1.5rem" height="1.5rem" />
                      {filterConditions?.length! > 0 && (
                        <div
                          aria-label="Active sort conditions"
                          style={{
                            position: "absolute",
                            top: 0,
                            insetInlineEnd: 0,

                            width: "0.5rem",
                            height: "0.5rem",

                            borderRadius: "6.25rem",
                            background: "var(--Notification-notification-primary, #6BBBE3)",

                            transform: "translate(-50%, 50%)",

                            zIndex: 10,
                          }}
                        />
                      )}
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}
              {enableSortingOption && (
                <div id={gridId ? `sorting-icon-${gridId}` : "sorting-icon"}>
                  <ToolTipComponent content={t("Sort")}>
                    <ButtonComponent
                      className="me-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={openSortingModal}
                      iconButton={true}
                    >
                      <IconComponent name="Sort" width="1.5rem" height="1.5rem" />

                      {sortConditions?.length! > 0 && (
                        <div
                          aria-label="Active sort conditions"
                          style={{
                            position: "absolute",
                            top: 0,
                            insetInlineEnd: 0,

                            width: "0.5rem",
                            height: "0.5rem",

                            borderRadius: "6.25rem",
                            background: "var(--Notification-notification-primary, #6BBBE3)",

                            transform: "translate(-50%, 50%)",

                            zIndex: 10,
                          }}
                        />
                      )}
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}

              {(enableGroupingOption || enableFilteringOption || enableSortingOption) &&
                (enableColumnChooser || propertiesPanel) && <div className="w-px h-8 bg-[#666A84] mx-1" />}

              {enableRowResize && (
                <div id="row-resize-icon">
                  <ToolTipComponent content={t("Row Resize")}>
                    <ButtonComponent
                      className="me-2 ms-2"
                      buttonType={BUTTON_TYPES.TEXT_BUTTON}
                      onClick={openRowResizeModal}
                      iconButton={true}
                    >
                      {getRowIcon(currentRowSize)}
                    </ButtonComponent>
                  </ToolTipComponent>
                </div>
              )}

              {enableColumnChooser && (
                <ToolTipComponent content={t("Column Chooser")}>
                  <ButtonComponent
                    buttonType={BUTTON_TYPES.TEXT_BUTTON}
                    className="me-2"
                    iconButton={true}
                    onClick={() => setShowColumnChooser(true)}
                  >
                    <IconComponent name="Column_Chooser" width="1.5rem" height="1.5rem" />
                  </ButtonComponent>
                </ToolTipComponent>
              )}

              {propertiesPanel && (
                <ToolTipComponent content={t("Properties Panel")}>
                  <ButtonComponent
                    className="me-2"
                    buttonType={BUTTON_TYPES.TEXT_BUTTON}
                    iconButton={true}
                    disabled={propertiesDisabled}
                    onClick={handleToggleProperties}
                  >
                    <IconComponent
                      name={showPropertiesPanel ? "PropertiesPanelOpen" : "PropertiesPanelClosed"}
                      width="1.5rem"
                      height="1.5rem"
                    />
                  </ButtonComponent>
                </ToolTipComponent>
              )}
              {rightStickyArray?.map((item: ToolBarEntry, index: number) => {
                if (item.tooltip) {
                  return (
                    <ToolTipComponent
                      key={`end-scrollable-${item.id}-${index}`}
                      content={item.tooltip as TranslatedString}
                    >
                      {renderToolbarItem(item, index)}
                    </ToolTipComponent>
                  );
                }
                return renderToolbarItem(item, index);
              })}
            </div>

            {showOverFlowBtns && (
              <div className="shrink-0 flex items-center h-full cursor-pointer">
                <Button
                  sx={{
                    height: "100%",
                    minWidth: "2.25rem",
                    borderInlineStart: "1px solid #D1D5DB",
                    borderRadius: "0 0.5rem 0.5rem 0",
                    color: "#6B7280",
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#E5E7EB",
                    },
                  }}
                  onClick={() => handleArrowClick("forward")}
                  size="small"
                >
                  <ArrowForwardIosIcon sx={{ fontSize: "1.125rem" }} />
                </Button>
              </div>
            )}
          </div>
        )}
        {showColumnChooser && (
          <GridColumnChooser
            ref={gridRef as React.Ref<SlickGrid1Ref<GridRowBase>>}
            onSave={(cols: string[]) => {
              if (cols.length === 0) {
                alert(t("Please select at least one column"));
                return;
              }
              setShowColumnChooser(false);
              const slickGrid = gridRef.current?.slickGrid;
              if (!slickGrid) return;
              const existingColsWidth = slickGrid
                .getColumns()
                .reduce((widths: Record<string, unknown>, col: ToolBarColumn) => {
                  widths = {
                    ...widths,
                    [col.id!]: col.width,
                  };
                  return widths;
                }, {});

              const clipModeFormatter = (
                row: number,
                cell: number,
                value: unknown,
                columnDef: ToolBarColumn,
                dataContext: GridRowBase,
              ) => {
                if (dataContext?.__isDummy) return "";

                let resolvedValue = value;
                if (resolvedValue === undefined && columnDef?.field) {
                  resolvedValue = get(dataContext, columnDef.field);
                }
                const finalValue = columnDef._valueGetter ? columnDef._valueGetter(dataContext) : resolvedValue;

                if (stateRef!.current!.enableWrapText) {
                  const container = document.createElement("div");
                  container.style.whiteSpace = "pre-wrap";
                  container.style.wordBreak = "break-word";
                  container.style.overflow = "hidden";
                  container.textContent = (finalValue ?? "") as string;
                  return container;
                }
                return ellipseFormatterWithTooltip!({
                  value: finalValue,
                  column: columnDef,
                });
              };
              const buttoncssClass = "mdi mdi-dots-vertical text-slate-900 inline-block  cursor-pointer mt-0";

              const headerConfig = {
                buttons: [
                  {
                    cssClass: buttoncssClass,
                    command: "toggle-highlight",
                  },
                ],
              };

              const allColDefs = gridRef.current!.gridService.getAllColumnDefinitions();
              const excludedCols = allColDefs.filter(
                (c: ToolBarColumn) =>
                  c.excludeFromColumnPicker && !specialColumns!.some((sc: { id?: string | number }) => sc.id === c.id),
              );

              const finalCols = [
                ...specialColumns!,
                ...excludedCols,
                ...cols
                  .map((colId: string) => {
                    const col: ToolBarColumn | undefined = allColDefs.find((c: ToolBarColumn) => c.id === colId);
                    return col
                      ? {
                          ...col,
                          excelExportOptions: {
                            valueParserCallback: (dataObj: unknown, { dataContext }: { dataContext: GridRowBase }) =>
                              col._valueGetter ? col._valueGetter(dataContext) : _.get(dataContext, col.field!),
                          },
                          width: existingColsWidth[colId] || col.width,
                          formatter: col.formatterType
                            ? getFormatterType(col)
                            : col.clipMode || (!col.formatter && !col.formatterType)
                              ? clipModeFormatter
                              : col.formatter,
                          header: headerConfig,
                          sortable: true,
                          groupable: true,
                          filterable: true,
                          type: "string",
                          headerCssClass: (col.headerCssClass || "") + " custom-header-column",
                          initiallyHidden: false,
                        }
                      : null;
                  })
                  .filter(Boolean),
              ];
              // `.filter(Boolean)` does not narrow away the null arm of the map above, and the
              // per-column objects carry grid-only extras; both are stated here rather than reshaped.
              slickGrid.setColumns(finalCols as Column[]);
              onSaveColumns?.(finalCols);
              slickGrid.invalidate();
              slickGrid.render();
            }}
            onCancel={() => setShowColumnChooser(false)}
          />
        )}
        {modalState === "Filter" && (
          <FilterModal
            isOpen={modalState === "Filter"}
            onClose={() => {
              setTempCondition!(null);
              setModalState!("");
            }}
            columnDefinitions={
              (() => {
                const visibleColIds = new Set<PropertyKey | undefined>(
                  gridRef.current?.slickGrid?.getColumns()?.map((c: ToolBarColumn) => c.id || c.field) || [],
                );
                return columnDefinitions!.filter((col: ToolBarColumnDefinition) => {
                  const colId = col.id || col.field;

                  return visibleColIds.size === 0 || visibleColIds.has(colId);
                });
              })() as FilterModalProps["columnDefinitions"]
            }
            // The modal's condition rows brand `value` narrower than the toolbar's applied-filter
            // rows do (`unknown`), so the pair has to be asserted across as a unit to keep the
            // state setter usable in both directions.
            conditions={filterConditions as unknown[] & FilterModalProps["conditions"]}
            setConditions={setFilterConditions as FilterModalProps["setConditions"]}
            anchorEl={anchorEl || (modalAnchor as HTMLElement)}
            remoteDataSource={remoteDataSource as FilterModalProps["remoteDataSource"]}
            beforeDataBound={beforeDataBound as FilterModalProps["beforeDataBound"]}
            datasetForFetchingOptions={datasetForFetchingOptions!}
            query={query as FilterModalProps["query"]}
            callRemote={callRemote as FilterModalProps["callRemote"]}
            localData={!remoteDataSource}
            getUpdatedFilterConditions={getUpdatedFilterConditions!}
            applyFilterFunction={applyFilterFunction!}
            gridRef={gridRef}
            setAppliedFilters={setAppliedFilters!}
            appliedFilters={appliedFilters!}
            setFilterPredicates={setFilterPredicates as FilterModalProps["setFilterPredicates"]}
            setFilterConditionsObject={setFilterConditionsObject as FilterModalProps["setFilterConditionsObject"]}
            currentColumnId={currentColumnId}
            setTempCondition={setTempCondition as FilterModalProps["setTempCondition"]}
            tempCondition={tempCondition as FilterModalProps["tempCondition"]}
          />
        )}
        {modalState === "Group" && (
          <SortGroupModal
            isOpen={modalState === "Group"}
            anchorEl={anchorEl || (modalAnchor as HTMLElement)}
            onClose={() => setModalState!("")}
            columnDefinitions={columnDefinitions as SortGroupModalProps["columnDefinitions"]}
            // The modal requires a direction on every row; a group condition may omit one.
            conditions={groupConditions as unknown[] & SortGroupModalProps["conditions"]}
            setConditions={setGroupConditions as SortGroupModalProps["setConditions"]}
            toggleGrouping={toggleGrouping}
            title="Group"
          />
        )}
        {showRowResize && (
          <RowResizeModal
            gridRef={gridRef}
            anchorEl={anchorEl}
            handleRowResizeOptionClick={handleRowResizeOptionClick}
            setEnableWrapText={setEnableWrapText}
            currentRowSize={currentRowSize}
            groupConditions={groupConditions}
            enableWrapText={enableWrapText}
            setCurrentRowSize={setCurrentRowSize}
            onClose={() => {
              setShowRowResize(false);
              setAnchorEl(null);
            }}
          />
        )}

        {modalState === "Sort" && (
          <SortGroupModal
            isOpen={modalState === "Sort"}
            anchorEl={anchorEl || (modalAnchor as HTMLElement)}
            onClose={() => setModalState!("")}
            columnDefinitions={columnDefinitions as SortGroupModalProps["columnDefinitions"]}
            conditions={sortConditions!}
            setConditions={setSortConditions!}
            title="Sort"
          />
        )}
      </>
    );
  },
);
ToolBar.displayName = "ToolBar";

export default ToolBar;
